import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Calendar,
  User,
  Clock,
  ArrowLeft,
  Share2,
} from "lucide-react";
import newsData from "./news-data.json";
import "./news.css";
import Footer from "../../../components/footer";
import Navbar from "../../../components/navbar";

function NewsArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    const foundArticle = newsData.find((item) => item.slug === slug);
    if (foundArticle) {
      setArticle(foundArticle);
      document.title = `${foundArticle.title} - Cheda Gaming`;
    }
    setLoading(false);
  }, [slug]);

  const formatDate = (dateString: string): string => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const formatContent = (content) => {
    return content.split("\n").map((paragraph, index) => {
      if (paragraph.startsWith("##")) {
        return (
          <h2 key={index} className="article-subtitle">
            {paragraph.replace("##", "")}
          </h2>
        );
      } else if (paragraph.startsWith("###")) {
        return (
          <h3 key={index} className="article-sub-subtitle">
            {paragraph.replace("###", "")}
          </h3>
        );
      } else if (paragraph.startsWith("•")) {
        return (
          <li key={index} className="article-list-item">
            {paragraph.replace("•", "")}
          </li>
        );
      } else if (paragraph.trim() === "") {
        return <br key={index} />;
      } else {
        return (
          <p key={index} className="article-paragraph">
            {paragraph}
          </p>
        );
      }
    });
  };

  if (loading) {
    return (
      <div className="news-article-page page-with-navbar-footer">
        <Navbar />
        <div className="news-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading article...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="news-article-page page-with-navbar-footer">
        <Navbar />
        <div className="news-container">
          <div className="error-container">
            <h2>Article Not Found</h2>
            <p>The article you're looking for doesn't exist.</p>
            <Link to="/news" className="back-to-news-btn">
              <ArrowLeft />
              Back to News
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="news-article-page page-with-navbar-footer">
      <Navbar />
      <div className="news-container">

        {/* Article Header */}
        <div className="article-header">
          <div className="article-category">{article.category}</div>
          <h1 className="article-title">{article.title}</h1>

          <div className="article-meta">
            <span className="article-meta-item">
              <Calendar />
              {formatDate(article.date)}
            </span>
            <span className="article-meta-item">
              <User />
              {article.author}
            </span>
            <span className="article-meta-item">
              <Clock />
              {article.readTime}
            </span>
          </div>
        </div>

        {/* Article Image or Gallery */}
        <div className="article-image-container">
          {Array.isArray(article.images) && article.images.length > 1 ? (
            <div className="article-gallery">
              <button
                className="gallery-arrow left"
                onClick={() =>
                  setGalleryIndex(
                    (galleryIndex - 1 + article.images.length) %
                      article.images.length,
                  )
                }
                aria-label="Previous image"
              >
                &#8592;
              </button>
              <img
                src={article.images[galleryIndex]}
                alt={article.title + " image " + (galleryIndex + 1)}
                className="article-image"
                onError={(e) => {
                  e.currentTarget.src = "/images/default-news.jpg";
                }}
              />
              <button
                className="gallery-arrow right"
                onClick={() =>
                  setGalleryIndex((galleryIndex + 1) % article.images.length)
                }
                aria-label="Next image"
              >
                &#8594;
              </button>
              <div className="gallery-indicators">
                {article.images.map((img, idx) => (
                  <span
                    key={img}
                    className={galleryIndex === idx ? "active" : ""}
                    onClick={() => setGalleryIndex(idx)}
                  >
                    ●
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <img
              src={article.image}
              alt={article.title}
              className="article-image"
              onError={(e) => {
                e.currentTarget.src = "/images/default-news.jpg";
              }}
            />
          )}
        </div>

        {/* Article Content */}
        <div className="article-content">
          <div className="article-body">{formatContent(article.content)}</div>

          {/* Share Section */}
          <div className="article-share">
            <h3>Share this article</h3>
            <div className="share-buttons">
              <button
                className="share-btn"
                onClick={() =>
                  navigator.share({
                    title: article.title,
                    url: window.location.href,
                  })
                }
              >
                <Share2 />
                Share
              </button>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        <div className="related-articles">
          <h3>More News</h3>
          <div className="related-grid">
            {newsData
              .filter((item) => item.id !== article.id)
              .slice(0, 3)
              .map((relatedArticle) => (
                <Link
                  key={relatedArticle.id}
                  to={`/news/${relatedArticle.slug}`}
                  className="related-card"
                >
                  <div className="related-image">
                    <img
                      src={relatedArticle.image}
                      alt={relatedArticle.title}
                      onError={(e) => {
                        e.currentTarget.src = "/images/default-news.jpg";
                      }}
                    />
                  </div>
                  <div className="related-content">
                    <h4>{relatedArticle.title}</h4>
                    <p>{relatedArticle.excerpt}</p>
                    <span className="related-date">
                      {formatDate(relatedArticle.date)}
                    </span>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default NewsArticle;
